import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApolloError } from '@apollo/client';
import { useGetEventByNameForPeoplePickerQuery } from '@graphql/generated';
import type { PersonFragment } from '@graphql/generated';
import type { LabelIdsToPeopleIdsMap, LabelViewItem, PersonViewItem, PersonItemRender } from './PeoplePicker';
import loadable from '@loadable/component';
import { useOverlayUnstable } from '@withjoy/joykit';
import { getInGroupPosition } from '@shared/utils/groupedListUtils';
import { isEqual } from 'lodash-es';
import { withWindow } from '@shared/utils/withWindow';
import 'libphonenumber-js-utils';
import { useTranslation } from '@shared/core';
import type { PeoplePickerUseCase } from './PeoplePicker.types';
import { usePeopleContext } from '@shared/context/people';

const PeoplePickerDialog = loadable(() => import('./PeoplePicker.dialog'));

export type SelectedPerson = Readonly<Partial<PersonFragment & { full_name: string }>>;

export type HardcodedLabel = Readonly<{
  id: string;
  display: string;
  people: ReadonlyArray<{ id: string; personIdTag: string }>;
  initiallySelected?: boolean;
  labelDescription?: string;
}>;

export type StoredSelectedPerson = SelectedPerson &
  Readonly<{
    hasEmailDeliveryMethodSelected?: boolean;
    hasTextDeliveryMethodSelected?: boolean;
  }>;

type UsePeoplePickerButtonControllerProps = Readonly<{
  title: React.ReactNode;
  eventHandle: string;
  onSelectedPeople?: (people: ReadonlyArray<StoredSelectedPerson>) => void;
  enableSmsSelection?: boolean;
  hardcodedLabels?: ReadonlyArray<HardcodedLabel>;
  personItemRender?: PersonItemRender;
  useCase: PeoplePickerUseCase;
}>;

type UsePeoplePickerButtonControllerReturn = Readonly<{
  isPeoplePickerSaving: boolean;
  loading: boolean;
  error: ApolloError | undefined;
  selectedPeople: ReadonlyArray<StoredSelectedPerson>;
  startSelection: () => void;
  viewPeople: ReadonlyArray<PersonViewItem>;
  viewLabels: ReadonlyArray<LabelViewItem>;
}>;

export const usePeoplePickerButtonController = ({
  eventHandle,
  enableSmsSelection = false,
  title,
  onSelectedPeople,
  hardcodedLabels,
  personItemRender,
  useCase,
}: UsePeoplePickerButtonControllerProps): UsePeoplePickerButtonControllerReturn => {
  const { tq } = useTranslation('peoplePickerButton');

  const { selectedPeople, setSelectedPeople } = usePeopleContext();
  const [isPeoplePickerSaving, setIsPeoplePickerSaving] = useState(false);
  const [viewLabels, setViewLabels] = useState<ReadonlyArray<LabelViewItem>>([]);
  const [viewPeople, setViewPeople] = useState<ReadonlyArray<PersonViewItem>>([]);
  const [labelIdsToPeopleIdsMap, setLabelIdsToPeopleIdsMap] = useState<LabelIdsToPeopleIdsMap>({});
  const [hasStartedSelection, setHasStartedSelection] = useState(false);

  const { data, error, loading } = useGetEventByNameForPeoplePickerQuery({
    batchMode: undefined,
    variables: { eventHandle },
  });

  // only api labels are filtered out if they have no people
  const apiLabels = useMemo<ReadonlyArray<HardcodedLabel>>(
    () => hardcodedLabels || data?.eventByName?.labels.filter(label => label.people && label.people.length > 0) || [],
    [data, hardcodedLabels],
  );

  const apiHouseholds = useMemo(() => {
    return data?.eventByName?.guests.households || [];
  }, [data]);

  const isPersonSelected = useCallback(
    (person: PersonFragment): boolean => {
      return selectedPeople.some(selectedPerson => person.id === selectedPerson.id || person.personIdTag === selectedPerson.personIdTag);
    },
    [selectedPeople],
  );

  const getMatchingStoredSelectedPerson = useCallback(
    (person: PersonFragment): StoredSelectedPerson | undefined => {
      return selectedPeople.find(selectedPerson => person.id === selectedPerson.id || person.personIdTag === selectedPerson.personIdTag);
    },
    [selectedPeople],
  );

  useEffect(() => {
    const newLabelIdsToPeopleIdsMap: Record<string, Array<string>> = {};
    const newViewLabels = apiLabels.map((label): LabelViewItem => {
      newLabelIdsToPeopleIdsMap[label.id] = label.people.map(person => person.id);
      return {
        id: label.id,
        selected: !!label.initiallySelected,
        labelText: `${label.display} (${label.people.length})`,
        disabled: label.people.length === 0,
        labelDescription: label.labelDescription,
      };
    });

    const newViewPeople = apiHouseholds.reduce((acc, household) => {
      const householdPersons = household.members.map((person, index): PersonViewItem => {
        const hasValidPhoneNumber = withWindow(global => {
          return !!global?.intlTelInputUtils.isValidNumber(person.phoneDisplay ?? '') && !/[^\u0000-\u00ff]/.test(person.phoneDisplay ?? '');
        }, false);

        const isDisabled = enableSmsSelection ? !(person.hasEmail || hasValidPhoneNumber) : !person.hasEmail;
        const storedSelectedPerson = getMatchingStoredSelectedPerson(person);

        const selected = isPersonSelected(person);
        return {
          id: person.id,
          personIdTag: person.personIdTag || undefined,
          selected,
          labelText: [person.firstName, person.lastName].filter(name => !!name).join(' '),
          sublabelText: enableSmsSelection ? undefined : `${person.hasEmail ? '' : tq`Missing email address`}`,
          disabled: isDisabled,
          groupId: person.household!.id,
          avatarName: person.avatarName || '',
          inGroupPosition: getInGroupPosition(index, household.members.length),
          email: person.email || '',
          hasPhoneNumber: hasValidPhoneNumber,
          // hasEmailDeliveryMethodSelected would be initially undefined. However, the person may initially be selected if selectedGuests is taken from localStorage. Same for hasEmailDeliveryMethodSelected
          hasEmailDeliveryMethodSelected: storedSelectedPerson?.hasEmailDeliveryMethodSelected ?? (selected && person.hasEmail),
          hasTextDeliveryMethodSelected: storedSelectedPerson?.hasTextDeliveryMethodSelected,
        };
      });
      return [...acc, ...householdPersons];
    }, [] as ReadonlyArray<PersonViewItem>);

    if (!isEqual(newViewLabels, viewLabels)) {
      setViewLabels(newViewLabels);
    }
    if (!isEqual(newViewPeople, viewPeople)) {
      setViewPeople(newViewPeople);
    }
    if (!isEqual(newLabelIdsToPeopleIdsMap, labelIdsToPeopleIdsMap)) {
      setLabelIdsToPeopleIdsMap(newLabelIdsToPeopleIdsMap);
    }
  }, [
    enableSmsSelection,
    apiLabels,
    apiHouseholds,
    isPersonSelected,
    setViewLabels,
    setViewPeople,
    viewLabels,
    viewPeople,
    labelIdsToPeopleIdsMap,
    getMatchingStoredSelectedPerson,
    tq,
  ]);

  // a hack to add person ids when passing selectedPeople array from local stroage (it only has personIdTags, we need ids)
  useEffect(() => {
    if (viewPeople?.length) {
      const newSelectedPeople = viewPeople
        .filter(person => person.selected && !person.disabled)
        .map(person => ({
          id: person.id,
          personIdTag: person.personIdTag,

          // Need to retain the two following for use in `useSendEmailHandlers`
          hasEmailDeliveryMethodSelected: person.hasEmailDeliveryMethodSelected,
          hasTextDeliveryMethodSelected: person.hasTextDeliveryMethodSelected,
        }));

      setSelectedPeople(newSelectedPeople);
    }
  }, [viewPeople, setSelectedPeople]);

  const submit = ({ mergedSelectionPeople }: { mergedSelectionPeople: ReadonlyArray<PersonViewItem> }) => {
    setIsPeoplePickerSaving(true);
    const newSelectedPeople = mergedSelectionPeople
      .filter(person => person.selected)
      .map(person => ({
        id: person.id,
        personIdTag: person.personIdTag,
        full_name: person.labelText,
        email: person.email,
        firstName: person.firstName,
        lastName: person.lastName,

        // Collect the following two fields to determine what method we should use to contact the person
        hasEmailDeliveryMethodSelected: person.hasEmailDeliveryMethodSelected,
        hasTextDeliveryMethodSelected: person.hasTextDeliveryMethodSelected,
      }));
    if (onSelectedPeople) {
      onSelectedPeople(newSelectedPeople);
    }
    // it is used only specifically for the invitations/save the dates case, as no labels selection should be saved - only people
    setSelectedPeople(newSelectedPeople);
    // this should be the last step, since unmounting the component create a race condition and stale data of selected people
    hidePeoplePicker();
  };

  const [showPeoplePicker, hidePeoplePicker] = useOverlayUnstable(
    {
      overlayKey: 'people_picker',
      render: overlayProps => {
        if (hasStartedSelection) {
          return (
            <PeoplePickerDialog
              enableSmsSelection={enableSmsSelection}
              tagsTitle={hardcodedLabels ? tq`Filters` : tq`My Tags`}
              personItemRender={personItemRender}
              {...overlayProps}
            />
          );
        }

        return <></>;
      },
      props: {
        title,
        eventHandle,
        isLoading: isPeoplePickerSaving,
        allPeople: viewPeople,
        allLabels: viewLabels,
        labelIdsToPeopleIdsMap,
        submit,
        useCase,
        hasStartedSelection,
      },
    },
    { dependencies: [viewPeople, viewLabels, isPeoplePickerSaving, hasStartedSelection] },
  );

  const startSelection = useCallback(() => {
    setHasStartedSelection(true);
    showPeoplePicker();
  }, [showPeoplePicker]);

  return {
    isPeoplePickerSaving,
    loading,
    error,
    selectedPeople,
    startSelection,
    viewPeople,
    viewLabels,
  };
};
